// src/components/MapComponent.js - УПРОЩЕННАЯ ВЕРСИЯ С ПРОПСАМИ
import React, { useRef, useEffect, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as h3 from 'h3-js';
import './MapComponent.css';
import { createMAPIMapStyle, analyzeMAPIPOI } from '../services/mapConfig';
import { createMAPIBuildings3D } from '../services/buildings3D';
import { TourController } from '../mapShowcase';
import { AutoSearchController } from '../autoSearchShowcase';
import { H3POIDensityLayer } from './H3POIDensityLayer';
import { HeatmapLayer } from './HeatmapLayer';
import H3ControlPanel from './H3ControlPanel';
import HeatmapControlPanel from './HeatmapControlPanel';
import { DroneController } from './droneFlyover';
import { DePINController } from '../components/DePINVideoRecorder';

function MapComponent({
  onCoordinatesChange,
  selectedPlace,
  showH3Grid,
  h3Resolution,
  simplifiedMode = false, // 🔥 НОВЫЙ ПРОПС
  hideControls = false,   // 🔥 НОВЫЙ ПРОПС
  hideH3Controls = false, // 🔥 НОВЫЙ ПРОПС
  hideHeatmapControls = false, // 🔥 НОВЫЙ ПРОПС
  hideTourControls = false, // 🔥 НОВЫЙ ПРОПС
  hideAutoSearchControls = false, // 🔥 НОВЫЙ ПРОПС
  hideDepinControls = false, // 🔥 НОВЫЙ ПРОПС
  hideDroneControls = false // 🔥 НОВЫЙ ПРОПС
    // forceShowLoading = false // 🔥 НОВЫЙ ПРОПС
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const tourController = useRef(null);
  const [lng] = useState(-73.9884);
  const [lat] = useState(40.7509);
  const [zoom] = useState(13.5);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapStatus, setMapStatus] = useState('initializing');
  const [mapError, setMapError] = useState(null);

  // H3 POI Density Layer состояние
  const [h3DensityLayer, setH3DensityLayer] = useState(null);
  const [showH3Controls, setShowH3Controls] = useState(false);
  const [h3Settings, setH3Settings] = useState({
    enabled: false,
    resolution: 8,
    showStats: false
  });

  // Heatmap Layer состояние  
  const [heatmapLayer, setHeatmapLayer] = useState(null);
  const [showHeatmapControls, setShowHeatmapControls] = useState(false);
  const [heatmapSettings, setHeatmapSettings] = useState({
    enabled: false,
    dataset: 'schools',
    propertyField: 'students'
  });

  useEffect(() => {
    if (map.current) return;

    const initializeMap = async () => {
      console.log('🗺️ Initializing MAPI map...');
      setMapStatus('loading style');
      setMapError(null);
      
      try {
        const mapStyle = await createMAPIMapStyle();
        
        setMapStatus('creating map');
        
        map.current = new maplibregl.Map({
          container: mapContainer.current,
          style: mapStyle,
          center: [lng, lat],
          zoom: zoom,
          pitch: 45,
          bearing: 0,
          maxZoom: 18,
          antialias: true,
          attributionControl: false,
          pixelRatio: 1
        });

        map.current.on('load', () => {
          analyzeMAPIPOI(map.current);
          console.log('✅ MAPI map loaded successfully!');
          setMapLoaded(true);
          setMapStatus('loaded');

          // 🔥 УСЛОВНАЯ ИНИЦИАЛИЗАЦИЯ ТОЛЬКО ЕСЛИ НЕ УПРОЩЕННЫЙ РЕЖИМ
          if (!simplifiedMode && !hideControls) {
            // 3D здания
            setTimeout(() => {
              console.log('🏗️ Initializing 3D buildings...');
              const buildingsLayerId = createMAPIBuildings3D(map.current);
              if (buildingsLayerId) {
                console.log('✅ 3D buildings ready!');
              }

              // Видео-тур
              if (!hideTourControls) {
                setTimeout(() => {
                  console.log('🎬 Initializing video tour...');
                  try {
                    if (!tourController.current) {
                      tourController.current = new TourController(map.current);
                      console.log('✅ Видео-тур готов!');
                      window.mapTour = tourController.current;
                    }
                  } catch (error) {
                    console.error('❌ Tour initialization failed:', error);
                  }
                }, 500);
              }

              // Автопоиск
              if (!hideAutoSearchControls) {
                setTimeout(() => {
                  console.log('🤖 Initializing auto search...');
                  try {
                    if (!window.autoSearchController) {
                      window.autoSearchController = new AutoSearchController(map.current);
                      console.log('✅ Автопоиск готов!');
                    }
                  } catch (error) {
                    console.error('❌ Auto search initialization failed:', error);
                  }
                }, 800);
              }

              // H3 POI Density Layer
              if (!hideH3Controls) {
                setTimeout(() => {
                  console.log('🌍 Initializing H3 POI Density Layer...');
                  try {
                    const h3Layer = new H3POIDensityLayer(map.current);
                    setH3DensityLayer(h3Layer);
                    console.log('✅ H3 POI Density Layer готов!');
                  } catch (error) {
                    console.error('❌ H3 Layer initialization failed:', error);
                  }
                }, 1200);
              }

              // Heatmap Layer
              if (!hideHeatmapControls) {
                setTimeout(() => {
                  console.log('🌡️ Initializing Heatmap Layer...');
                  try {
                    const heatmap = new HeatmapLayer(map.current, {
                      dataset: 'schools',
                      propertyField: 'students'
                    });
                    setHeatmapLayer(heatmap);
                    console.log('✅ Heatmap Layer готов!');
                  } catch (error) {
                    console.error('❌ Heatmap Layer initialization failed:', error);
                  }
                }, 1500);
              }

            }, 1000);
          }
        });

        map.current.on('error', (e) => {
          console.error('❌ Map error:', e.error);
          setMapStatus('error');
          setMapError(e.error?.message || 'Unknown map error');
        });

        map.current.on('move', () => {
          if (map.current && onCoordinatesChange) {
            const center = map.current.getCenter();
            onCoordinatesChange({
              lng: center.lng.toFixed(6),
              lat: center.lat.toFixed(6)
            });
          }
        });

        map.current.on('click', (e) => {
          const { lng, lat } = e.lngLat;
          console.log('📍 Map clicked:', lat.toFixed(6), lng.toFixed(6));
          if (onCoordinatesChange) {
            onCoordinatesChange({
              lng: lng.toFixed(6),
              lat: lat.toFixed(6)
            });
          }
        });

      } catch (error) {
        console.error('❌ MAPI map initialization failed:', error);
        setMapStatus('error');
        setMapError(error.message);
      }
    };

    initializeMap();

    return () => {
      if (map.current) {
        console.log('🧹 Cleaning up MAPI map...');
        
        if (tourController.current) {
          try {
            tourController.current.showcase.stopTour();
            tourController.current = null;
          } catch (error) {
            console.warn('⚠️ Tour cleanup error:', error);
          }
        }

        if (h3DensityLayer) {
          h3DensityLayer.destroy();
          setH3DensityLayer(null);
        }

        if (heatmapLayer) {
          heatmapLayer.destroy();
          setHeatmapLayer(null);
        }
        
        map.current.remove();
        map.current = null;
      }
    };
  }, [lng, lat, zoom, onCoordinatesChange, simplifiedMode, hideControls, hideH3Controls, hideHeatmapControls, hideTourControls, hideAutoSearchControls]);

  useEffect(() => {
    if (map.current && selectedPlace && mapLoaded && selectedPlace.coordinates) {
      console.log('🎯 Flying to selected place:', selectedPlace);
      
      let zoom = 15;
      let pitch = 45;
      
      const placeName = selectedPlace.name?.toLowerCase() || '';
      const region = selectedPlace.region?.toLowerCase() || '';
      const country = selectedPlace.country?.toLowerCase() || '';
      
      if (placeName.includes('building') || 
          placeName.includes('house') || 
          placeName.includes('street') ||
          placeName.includes('avenue') ||
          placeName.includes('road') ||
          /\d+/.test(placeName)) {
        zoom = 17.7;
        pitch = 45;
      }
      else if (selectedPlace.region === selectedPlace.name || 
               placeName.includes('city') ||
               placeName.includes('town') ||
               placeName.includes('village')) {
        zoom = 11.7;
        pitch = 30;
      }
      else if (selectedPlace.country === selectedPlace.name ||
               placeName === country) {
        zoom = 5.5;
        pitch = 0;
      }
      else if (placeName.includes('region') ||
               placeName.includes('state') ||
               placeName.includes('province') ||
               placeName.includes('oblast') ||
               region === placeName) {
        zoom = 8.5;
        pitch = 15;
      }
      else if (placeName.includes('district') ||
               placeName.includes('neighborhood') ||
               placeName.includes('quarter') ||
               placeName.includes('ward')) {
        zoom = 14.5;
        pitch = 35;
      }
      else if (placeName.includes('park') ||
               placeName.includes('square') ||
               placeName.includes('market') ||
               placeName.includes('station') ||
               placeName.includes('airport') ||
               placeName.includes('hospital') ||
               placeName.includes('school') ||
               placeName.includes('university') ||
               placeName.includes('museum') ||
               placeName.includes('theater') ||
               placeName.includes('church') ||
               placeName.includes('mall') ||
               placeName.includes('center')) {
        zoom = 16;
        pitch = 40;
      }
      else {
        zoom = 17.7;
        pitch = 45;
      }
      
      console.log(`🎯 ${selectedPlace.name}: zoom=${zoom}, pitch=${pitch}`);
      
      map.current.flyTo({
        center: selectedPlace.coordinates,
        zoom: zoom,
        pitch: pitch,
        duration: 1000
      });
    }
  }, [selectedPlace, mapLoaded]);

  // ОРИГИНАЛЬНЫЙ H3 GRID (простые гексагоны)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const H3_SOURCE_ID = 'h3-hex-source';
    const H3_LAYER_ID = 'h3-hex-layer';

    const removeH3Layer = () => {
      if (map.current.getLayer(H3_LAYER_ID)) map.current.removeLayer(H3_LAYER_ID);
      if (map.current.getLayer(`${H3_LAYER_ID}-border`)) map.current.removeLayer(`${H3_LAYER_ID}-border`);
      if (map.current.getSource(H3_SOURCE_ID)) map.current.removeSource(H3_SOURCE_ID);
    };

    if (showH3Grid) {
      removeH3Layer();
      try {
        const center = map.current.getCenter();
        const h3Index = h3.latLngToCell(center.lat, center.lng, h3Resolution);
        const h3Boundary = h3.cellToBoundary(h3Index, true);

        map.current.addSource(H3_SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              properties: { h3Index, resolution: h3Resolution },
              geometry: { type: 'Polygon', coordinates: [h3Boundary] }
            }]
          }
        });

        map.current.addLayer({
          id: H3_LAYER_ID,
          type: 'fill',
          source: H3_SOURCE_ID,
          paint: { 'fill-color': '#8b5cf6', 'fill-opacity': 0.3 }
        });

        map.current.addLayer({
          id: `${H3_LAYER_ID}-border`,
          type: 'line',
          source: H3_SOURCE_ID,
          paint: { 'line-color': '#7c3aed', 'line-width': 2, 'line-opacity': 0.8 }
        });

        console.log('✅ H3 grid added for resolution:', h3Resolution);

      } catch (error) {
        console.error('❌ H3 grid error:', error);
      }
    } else {
      removeH3Layer();
    }
  }, [showH3Grid, h3Resolution, mapLoaded]);

  const startTour = () => {
    if (tourController.current) {
      tourController.current.showcase.startTour();
    } else {
      console.warn('⚠️ Tour not initialized yet');
    }
  };

  const stopTour = () => {
    if (tourController.current) {
      tourController.current.showcase.stopTour();
    }
  };

  const handleH3SettingsChange = (newSettings) => {
    console.log('🌍 H3 settings changed:', newSettings);
    setH3Settings(prev => ({ ...prev, ...newSettings }));
    
    if (h3DensityLayer && h3DensityLayer.enabled !== newSettings.enabled) {
      h3DensityLayer.toggle(newSettings.enabled);
    }
    
    if (h3DensityLayer && newSettings.resolution && h3DensityLayer.resolution !== newSettings.resolution) {
      h3DensityLayer.setResolution(newSettings.resolution);
    }
  };

  const handleHeatmapSettingsChange = (newSettings) => {
    console.log('🌡️ Heatmap settings changed:', newSettings);
    setHeatmapSettings(prev => ({ ...prev, ...newSettings }));
  };

  // 🔥 УСЛОВНАЯ ИНИЦИАЛИЗАЦИЯ DRONE КОНТРОЛЛЕРА
  useEffect(() => {
    if (map.current && !window.droneInitialized && !simplifiedMode && !hideDroneControls) {
      const timer = setTimeout(() => {
        console.log('🚁 Initializing DRONE Flyover...');
        try {
          window.droneController = new DroneController(map.current);
          window.droneInitialized = true;
          console.log('✅ DRONE ready!');
        } catch (error) {
          console.error('❌ DRONE initialization failed:', error);
        }
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [map.current, simplifiedMode, hideDroneControls]);

  // 🔥 УСЛОВНАЯ ИНИЦИАЛИЗАЦИЯ DePIN КОНТРОЛЛЕРА
  useEffect(() => {
    if (map.current && !window.depinInitialized && !simplifiedMode && !hideDepinControls) {
      const timer = setTimeout(() => {
        console.log('🌐 Initializing DePIN Controller...');
        try {
          window.depinController = new DePINController(map.current);
          window.depinInitialized = true;
          console.log('✅ DePIN ready!');
        } catch (error) {
          console.error('❌ DePIN initialization failed:', error);
        }
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [map.current, simplifiedMode, hideDepinControls]);

  return (
    <div className="map-wrapper">
      {/* 🔥 УСЛОВНЫЙ РЕНДЕРИНГ КОНТРОЛОВ */}
      
      {/* H3 Controls Overlay */}
      {!simplifiedMode && !hideH3Controls && showH3Controls && h3DensityLayer && (
        <div className="h3-controls-overlay">
          <H3ControlPanel 
            h3Layer={h3DensityLayer}
            onSettingsChange={handleH3SettingsChange}
            currentSettings={h3Settings}
          />
        </div>
      )}

      {/* Heatmap Controls Overlay */}
      {!simplifiedMode && !hideHeatmapControls && showHeatmapControls && heatmapLayer && (
        <div className="heatmap-controls-overlay">
          <HeatmapControlPanel 
            heatmapLayer={heatmapLayer}
            onSettingsChange={handleHeatmapSettingsChange}
            currentSettings={heatmapSettings}
          />
        </div>
      )}
      
      <div ref={mapContainer} className="map-container">
        {(!mapLoaded ) && (
          <div className="mapi-map-loading">
            <div className="mapi-loading-spinner">
              <div className="mapi-loading-dot"></div>
              <div className="mapi-loading-dot"></div>
              <div className="mapi-loading-dot"></div>
              <div className="mapi-loading-dot"></div>
              <div className="mapi-loading-dot"></div>
              <div className="mapi-loading-dot"></div>
              <div className="mapi-loading-dot"></div>
              <div className="mapi-loading-dot"></div>
            </div>
          </div>
        )}
        
        {mapLoaded && (
          <div style={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            background: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '5px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            zIndex: 1000
          }}>
            mapi
          </div>
        )}

        {/* 🔥 УСЛОВНЫЕ КНОПКИ УПРАВЛЕНИЯ */}
        
        {/* Кнопка тура */}
        {!simplifiedMode && !hideTourControls && mapLoaded && tourController.current && (
          <div className="tour-controls">
            <button onClick={startTour} className="tour-btn">
              🎬 Старт тура
            </button>
          </div>
        )}

        {/* Кнопка управления H3 */}
        {!simplifiedMode && !hideH3Controls && mapLoaded && h3DensityLayer && (
          <div className="h3-controls">
            <button 
              className={`h3-control-toggle ${showH3Controls ? 'active' : ''}`}
              onClick={() => setShowH3Controls(!showH3Controls)}
              title="Toggle H3 POI Density Controls"
            >
              🌍 H3 POI
            </button>
            
            {h3Settings.enabled && (
              <div className="h3-status-indicator">
                <span className="h3-status-dot"></span>
                H3 Active (Res: {h3Settings.resolution})
              </div>
            )}
          </div>
        )}

        {/* Кнопка управления Heatmap */}
        {!simplifiedMode && !hideHeatmapControls && mapLoaded && heatmapLayer && (
          <div className="heatmap-controls">
            <button 
              className={`heatmap-control-toggle ${showHeatmapControls ? 'active' : ''}`}
              onClick={() => setShowHeatmapControls(!showHeatmapControls)}
              title="Toggle Heatmap Controls"
            >
              🌡️ Heatmap
            </button>
            
            {heatmapSettings.enabled && (
              <div className="heatmap-status-indicator">
                <span className="status-indicator active"></span>
                Heatmap Active ({heatmapSettings.dataset})
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MapComponent;